import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { editarBreadcrumb } from '../../../actions/breadcrumb';
import { obtenerOrden, limpiarDetalleOrden } from '../../../actions/ordenesSalida';

import EntregarOrdenModal from './modals/EntregarOrdenModal';
import CancelarOrdenModal from './modals/CancelarOrdenModal';
import EliminarOrdenModal from './modals/EliminarOrdenModal';
import DevolverOrdenCompletaModal from './modals/DevolverOrdenCompletaModal';

const formatDate = (value) => {
    if (!value) return 'Sin definir';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString('es-MX');
};

const formatMoney = (n) => {
    const v = Number(n || 0);
    return `$${new Intl.NumberFormat('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(v)}`;
};

const renderBadgeStatus = (status) => {
    const s = Number(status);
    if (s === 0) return <span className="badge bg-info text-dark">Creada</span>;
    if (s === 1) return <span className="badge text-dark" style={{ backgroundColor: '#fd7e14' }}>Semi-entregada</span>;
    if (s === 2) return <span className="badge bg-success">Entregada</span>;
    if (s === 3) return <span className="badge bg-warning text-dark">Cancelada</span>;
    if (s === 4) return <span className="badge bg-secondary">Eliminada</span>;
    return <span className="badge bg-secondary">Sin definir</span>;
};

const formatNumber = (value) => {
    const v = Number(value || 0);
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(v);
};

const buildUbicacionLabel = (mov) => {
    if (!mov) return '—';
    const parts = [];
    if (mov?.area?.nombre) parts.push(mov.area.nombre);
    if (mov?.organizador?.nombre) parts.push(mov.organizador.nombre);
    if (mov?.ubicacion?.nombre) parts.push(mov.ubicacion.nombre);
    return parts.join(' / ') || 'Sin ubicación';
};

const DetalleOrdenSalida = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const { detalle: orden, loadingDetalle } = useSelector(state => state.ordenesSalida);

    const [modalEntregar, setModalEntregar] = useState(false);
    const [modalCancelar, setModalCancelar] = useState(false);
    const [modalEliminar, setModalEliminar] = useState(false);
    const [modalDevolverCompleta, setModalDevolverCompleta] = useState(false);
    const [paquetesExpandidos, setPaquetesExpandidos] = useState({});

    const togglePaqueteExpandido = (key) => {
        setPaquetesExpandidos((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    useEffect(() => {
        if (!id) return;

        dispatch(obtenerOrden(id));

        return () => {
            dispatch(limpiarDetalleOrden());
        };
    }, [dispatch, id]);

    useEffect(() => {
        dispatch(editarBreadcrumb([
            { activo: false, nombre: 'Proyectos', path: '/proyectos' },
            { activo: false, nombre: orden?.proyecto?.nombre || 'Proyecto', path: `/proyectos/detalle/${orden?.proyecto?._id || ''}` },
            { activo: true, nombre: `Orden ${orden?.folio || ''}`.trim(), path: `/proyectos/ordenes/detalle/${id}` }
        ]));
    }, [dispatch, id, orden]);

    const recargar = () => {
        dispatch(obtenerOrden(id));
    };

    if (loadingDetalle || !orden) {
        return (
            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <h3 className="my-5">Cargando orden de salida...</h3>
                </div>
            </div>
        );
    }

    const productos = Array.isArray(orden.productos) ? orden.productos : [];
    const conceptos = Array.isArray(orden.conceptos) ? orden.conceptos : [];
    const movimientos = Array.isArray(orden.movimientos) ? orden.movimientos : [];
    const paquetes = Array.isArray(orden.paquetes) ? orden.paquetes : [];
    const status = Number(orden.status);
    const puedeDevolverCompleta = status === 2 && !!orden.devolucion_total;

    /* Solo movimientos de líneas sueltas — los de componentes de paquete (orden_salida_paquete
       distinto de null) se agrupan por separado más abajo, para no mezclar el "entregado" de un
       producto suelto con el de ese mismo producto cuando también es componente de un paquete. */
    const movimientosPorProducto = movimientos.reduce((mapa, mov) => {
        if (mov?.orden_salida_paquete) return mapa;
        const idProducto = mov?.producto?._id;
        if (!idProducto) return mapa;

        if (!mapa[idProducto]) mapa[idProducto] = [];
        mapa[idProducto].push(mov);

        return mapa;
    }, {});

    const movimientosPorPaqueteComponente = movimientos.reduce((mapa, mov) => {
        if (!mov?.orden_salida_paquete) return mapa;
        const idProducto = mov?.producto?._id;
        if (!idProducto) return mapa;

        const key = `${mov.orden_salida_paquete}__${idProducto}`;
        if (!mapa[key]) mapa[key] = [];
        mapa[key].push(mov);

        return mapa;
    }, {});

    return (
        <Fragment>
            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <div className="d-flex align-items-center gap-2 mb-1 mt-5">
                        <h3 className="mb-0">Orden {orden?.folio || ''}</h3>
                        {renderBadgeStatus(orden?.status)}
                        {status === 2 && !orden.devolucion_total && (
                            <span className="badge bg-secondary">Tiene devolución asociada</span>
                        )}
                    </div>
                    <h6 className="text-muted mb-4">{orden?.proyecto?.nombre || 'Proyecto'}</h6>

                    <div className="row mb-4">
                        <div className="col-md-3">
                            <div className="text-muted small">Fecha</div>
                            <div>{formatDate(orden?.fecha)}</div>
                        </div>
                        <div className="col-md-3">
                            <div className="text-muted small">Cotización de origen</div>
                            <div>
                                {orden?.cotizacion?._id ? (
                                    <button
                                        type="button"
                                        className="btn btn-link p-0"
                                        onClick={() => navigate(`/proyectos/cotizaciones/detalle/${orden.cotizacion._id}`)}
                                    >
                                        {orden.cotizacion.folio}
                                    </button>
                                ) : 'No aplica'}
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="text-muted small">Dirección</div>
                            <div>{orden?.proyecto?.direccion || 'Sin definir'}</div>
                        </div>
                        <div className="col-md-3">
                            <div className="text-muted small">Total</div>
                            <div className="fw-bold">{formatMoney(orden?.total)}</div>
                        </div>
                    </div>

                    <h5 className="mb-3">Productos</h5>

                    <div className="table-responsive mb-4">
                        <table className="table table-striped table-hover">
                            <thead>
                                <tr>
                                    <th>Producto</th>
                                    <th>Proveedor</th>
                                    <th>Unidad</th>
                                    <th className="text-end">Cantidad</th>
                                    <th className="text-end">Precio compra</th>
                                    <th className="text-end">Precio venta</th>
                                    <th className="text-end">Precio final</th>
                                    <th className="text-center">IVA</th>
                                    <th className="text-end">Importe</th>
                                    <th className="text-end">Pendiente</th>
                                    <th>Ubicación entregada</th>
                                </tr>
                            </thead>
                            <tbody>
                                {productos.length === 0 && (
                                    <tr>
                                        <td colSpan="11" className="text-center text-muted">
                                            Sin productos
                                        </td>
                                    </tr>
                                )}

                                {productos.map((p) => {
                                    const importe = Number(p.cantidad || 0) * Number(p.precio || 0) * (p.iva ? 1.16 : 1);
                                    const movimientosProducto = movimientosPorProducto[p.producto?._id] || [];
                                    const entregado = movimientosProducto.reduce((acc, mov) => acc + Number(mov?.cantidad || 0), 0);
                                    const pendiente = Math.max(0, Number(p.cantidad || 0) - entregado);

                                    return (
                                        <tr key={p._id}>
                                            <td>{p.producto?.nombre || 'Sin definir'}</td>
                                            <td>{p.proveedor?.nombre || '—'}</td>
                                            <td>{p.unidad?.nombre || '—'}</td>
                                            <td className="text-end">{p.cantidad}</td>
                                            <td className="text-end">{formatMoney(p.precio_compra)}</td>
                                            <td className="text-end">{formatMoney(p.precio_venta)}</td>
                                            <td className="text-end">{formatMoney(p.precio)}</td>
                                            <td className="text-center">{p.iva ? 'Sí' : 'No'}</td>
                                            <td className="text-end">{formatMoney(importe)}</td>
                                            <td className={`text-end fw-bold ${pendiente > 0 ? 'text-warning' : 'text-success'}`}>
                                                {formatNumber(pendiente)}
                                            </td>
                                            <td>
                                                {movimientosProducto.length === 0 ? (
                                                    '—'
                                                ) : (
                                                    <ul className="list-unstyled mb-0">
                                                        {movimientosProducto.map((mov) => (
                                                            <li key={mov._id}>
                                                                {buildUbicacionLabel(mov)}
                                                                <span className="text-muted"> · {formatNumber(mov.cantidad)}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <h5 className="mb-3">Conceptos</h5>

                    <div className="table-responsive mb-4">
                        <table className="table table-striped table-hover">
                            <thead>
                                <tr>
                                    <th>Tipo</th>
                                    <th>Detalle</th>
                                    <th className="text-end">Cantidad</th>
                                    <th>Unidad</th>
                                    <th className="text-end">Precio unitario</th>
                                    <th className="text-center">IVA</th>
                                    <th className="text-end">Importe</th>
                                </tr>
                            </thead>
                            <tbody>
                                {conceptos.length === 0 && (
                                    <tr>
                                        <td colSpan="7" className="text-center text-muted">
                                            Sin conceptos
                                        </td>
                                    </tr>
                                )}

                                {conceptos.map((c) => {
                                    const base = Number(c.cantidad || 0) * Number(c.precio_unitario || 0);
                                    const importe = c.iva ? base * 1.16 : base;
                                    return (
                                        <tr key={c._id}>
                                            <td>{Number(c.tipo) === 1 ? 'Producto' : 'Servicio'}</td>
                                            <td>{c.detalle}</td>
                                            <td className="text-end">{c.cantidad}</td>
                                            <td>{c.unidad?.nombre || '—'}</td>
                                            <td className="text-end">{formatMoney(c.precio_unitario)}</td>
                                            <td className="text-center">{c.iva ? 'Sí' : 'No'}</td>
                                            <td className="text-end">{formatMoney(importe)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <h5 className="mb-3">Paquetes</h5>

                    <div className="table-responsive mb-4">
                        <table className="table table-striped table-hover">
                            <thead>
                                <tr>
                                    <th style={{ width: 30 }} />
                                    <th>Paquete</th>
                                    <th className="text-end">Cantidad</th>
                                    <th className="text-end">Precio final</th>
                                    <th className="text-center">IVA</th>
                                    <th className="text-end">Importe</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paquetes.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="text-center text-muted">
                                            Sin paquetes
                                        </td>
                                    </tr>
                                )}

                                {paquetes.map((p) => {
                                    const cantidad = Number(p.cantidad || 0);
                                    const importe = cantidad * Number(p.precio || 0) * (p.iva ? 1.16 : 1);
                                    const componentes = Array.isArray(p.componentes) ? p.componentes : [];
                                    const abierto = !!paquetesExpandidos[p._id];

                                    return (
                                        <Fragment key={p._id}>
                                            <tr>
                                                <td className="text-center align-middle">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-link p-0"
                                                        onClick={() => togglePaqueteExpandido(p._id)}
                                                        title={abierto ? 'Ocultar productos del paquete' : 'Ver productos del paquete'}
                                                    >
                                                        <i className={`fas fa-chevron-${abierto ? 'down' : 'right'}`} />
                                                    </button>
                                                </td>
                                                <td>
                                                    <div className="fw-semibold">{p.paquete?.nombre || 'Sin definir'}</div>
                                                    {p.paquete?.codigo ? (
                                                        <div className="text-muted" style={{ fontSize: 12 }}>{p.paquete.codigo}</div>
                                                    ) : null}
                                                </td>
                                                <td className="text-end">{cantidad}</td>
                                                <td className="text-end">{formatMoney(p.precio)}</td>
                                                <td className="text-center">{p.iva ? 'Sí' : 'No'}</td>
                                                <td className="text-end">{formatMoney(importe)}</td>
                                            </tr>
                                            {abierto && (
                                            <tr>
                                                <td />
                                                <td colSpan={5} className="p-0">
                                                    <div className="table-responsive m-2">
                                                        <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                                                            <thead>
                                                                <tr>
                                                                    <th>Producto</th>
                                                                    <th style={{ width: 120 }}>Unidad</th>
                                                                    <th className="text-center" style={{ width: 90 }}>Cantidad</th>
                                                                    <th className="text-end" style={{ width: 90 }}>Pendiente</th>
                                                                    <th>Ubicación entregada</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {componentes.length === 0 ? (
                                                                    <tr>
                                                                        <td colSpan={5} className="text-muted text-center">Sin productos</td>
                                                                    </tr>
                                                                ) : componentes.map((c) => {
                                                                    const necesaria = Number(c.cantidad_nominal ?? c.cantidad ?? 0);
                                                                    const movimientosComponente = movimientosPorPaqueteComponente[`${p._id}__${c._id}`] || [];
                                                                    const entregado = movimientosComponente.reduce((acc, mov) => acc + Number(mov?.cantidad || 0), 0);
                                                                    const pendiente = Math.max(0, necesaria - entregado);

                                                                    return (
                                                                        <tr key={c._id}>
                                                                            <td>{c.nombre}</td>
                                                                            <td>{c.unidad?.nombre || '—'}</td>
                                                                            <td className="text-center">{necesaria}</td>
                                                                            <td className={`text-end fw-bold ${pendiente > 0 ? 'text-warning' : 'text-success'}`}>
                                                                                {formatNumber(pendiente)}
                                                                            </td>
                                                                            <td>
                                                                                {movimientosComponente.length === 0 ? (
                                                                                    '—'
                                                                                ) : (
                                                                                    <ul className="list-unstyled mb-0">
                                                                                        {movimientosComponente.map((mov) => (
                                                                                            <li key={mov._id}>
                                                                                                {buildUbicacionLabel(mov)}
                                                                                                <span className="text-muted"> · {formatNumber(mov.cantidad)}</span>
                                                                                            </li>
                                                                                        ))}
                                                                                    </ul>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </td>
                                            </tr>
                                            )}
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center">
                        <div className="fs-5">
                            <span className="text-muted">Total: </span>
                            <span className="fw-bold">{formatMoney(orden?.total)}</span>
                        </div>

                        <div className="d-flex gap-2">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => navigate(`/proyectos/detalle/${orden?.proyecto?._id || ''}`)}
                            >
                                Volver
                            </button>

                            {[0, 1, 2].includes(status) && (
                                <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    onClick={() => navigate(`/proyectos/ordenes/editar/${id}`)}
                                >
                                    <i className="fa fa-edit" /> {status === 0 ? 'Editar' : 'Editar precios'}
                                </button>
                            )}

                            {[0, 1].includes(status) && (
                                <button
                                    type="button"
                                    className="btn btn-warning"
                                    onClick={() => setModalCancelar(true)}
                                >
                                    Cancelar orden
                                </button>
                            )}

                            {[0, 1].includes(status) && (
                                <button
                                    type="button"
                                    className="btn btn-success"
                                    onClick={() => setModalEntregar(true)}
                                >
                                    <i className="fas fa-truck" /> {status === 1 ? 'Continuar entrega' : 'Realizar entrega'}
                                </button>
                            )}

                            {puedeDevolverCompleta && (
                                <button
                                    type="button"
                                    className="btn btn-outline-danger"
                                    onClick={() => setModalDevolverCompleta(true)}
                                >
                                    <i className="fas fa-undo" /> Devolución completa
                                </button>
                            )}

                            {[0, 3].includes(status) && (
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={() => setModalEliminar(true)}
                                >
                                    Eliminar
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <EntregarOrdenModal
                show={modalEntregar}
                orden={orden}
                onHide={() => setModalEntregar(false)}
                onSuccess={recargar}
            />

            <CancelarOrdenModal
                show={modalCancelar}
                idOrden={id}
                onHide={() => setModalCancelar(false)}
                onSuccess={recargar}
            />

            <EliminarOrdenModal
                show={modalEliminar}
                idOrden={id}
                idProyecto={orden?.proyecto?._id || null}
                onHide={() => setModalEliminar(false)}
                onSuccess={() => navigate(`/proyectos/detalle/${orden?.proyecto?._id || ''}`)}
            />

            <DevolverOrdenCompletaModal
                show={modalDevolverCompleta}
                idOrden={id}
                onHide={() => setModalDevolverCompleta(false)}
                onSuccess={() => navigate(`/proyectos/detalle/${orden?.proyecto?._id || ''}`)}
            />
        </Fragment>
    );
};

export default DetalleOrdenSalida;
